import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Cloud, CheckCircle2, XCircle, Loader2, Save, Eye, EyeOff,
  HelpCircle, ChevronDown, ChevronUp, ExternalLink, Copy, Link as LinkIcon,
  Sparkles, FolderTree,
} from 'lucide-react';

export default function GoogleSettingsCard({ googleStatus, onConnect, onDisconnect }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [form, setForm] = useState({ client_id: '', client_secret: '' });
  // Sync / migration state
  const [migrating, setMigrating] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [lastSummary, setLastSummary] = useState(null);
  const [progress, setProgress] = useState(null); // {running, step, percent, current, total, error}

  const loadSyncStatus = async () => {
    try {
      const r = await api.get('/google/sync/status');
      setSyncStatus(r.data);
      if (r.data?.last_migration_summary) setLastSummary(r.data.last_migration_summary);
      const p = r.data?.migration_progress || null;
      setProgress(p);
      if (p?.running) setMigrating(true);
      return r.data;
    } catch (_) { /* admin only */ }
    return null;
  };

  const load = async () => {
    try {
      const r = await api.get('/google/oauth-settings');
      setData(r.data);
      setForm((p) => ({ ...p, client_id: r.data.client_id || '' }));
    } catch (e) {
      // Non-admin — stay silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadSyncStatus(); }, []);

  // Poll status while migration is running
  useEffect(() => {
    if (!migrating) return;
    const id = setInterval(async () => {
      const s = await loadSyncStatus();
      const p = s?.migration_progress;
      if (p && !p.running) {
        setMigrating(false);
        if (p.error) {
          toast.error(`Migration échouée : ${p.error}`, { duration: 12000 });
        } else if (s?.last_migration_summary) {
          const sum = s.last_migration_summary;
          toast.success(
            `Migration terminée en ${Math.round(sum.duration_seconds || 0)}s — ` +
            `${sum.chantiers} chantiers, ${sum.contacts} clients, ` +
            `${sum.factures} factures émises, ${sum.factures_achat} achats.`,
            { duration: 12000 }
          );
        }
      }
    }, 2500);
    return () => clearInterval(id);
  }, [migrating]);

  const runMigration = async () => {
    if (!window.confirm(
      "Lancer la migration complète vers Google Drive & Sheets ?\n\n" +
      "Cela va :\n" +
      "• Créer l'arborescence Drive (Collectivités, Clients privés, Sous-traitants, Achats…)\n" +
      "• Créer un dossier par client et par chantier\n" +
      "• Créer le classeur \"Reno Resolve - Dashboard\" avec tous vos devis, factures, chantiers, etc.\n\n" +
      "La migration tourne en arrière-plan : vous pouvez fermer cette page, la progression sera conservée. Selon le volume, comptez 1 à 10 minutes. C'est idempotent — vous pouvez la relancer."
    )) return;
    setMigrating(true);
    setProgress({ running: true, step: "Démarrage…", percent: 0 });
    try {
      const r = await api.post('/google/sync/migrate', {});
      if (r.data?.running === false) {
        // Was already running, just pick up the live progress
        toast.info(r.data.message || "Migration déjà en cours");
      } else {
        toast.success("Migration démarrée en arrière-plan", { duration: 5000 });
      }
      await loadSyncStatus();
    } catch (e) {
      setMigrating(false);
      toast.error(e?.response?.data?.detail || "Échec du démarrage de la migration", { duration: 10000 });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { client_id: form.client_id.trim() };
      if (form.client_secret) body.client_secret = form.client_secret;
      await api.put('/google/oauth-settings', body);
      toast.success('Identifiants Google OAuth enregistrés');
      setForm((p) => ({ ...p, client_secret: '' }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
    setSaving(false);
  };

  const copyRedirectUri = () => {
    if (!data?.redirect_uri) return;
    navigator.clipboard.writeText(data.redirect_uri);
    toast.success("URI de redirection copiée");
  };

  if (loading || !data) return null;
  const connected = !!googleStatus?.connected;
  const oauthConfigured = data.configured;

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200" data-testid="google-settings-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Cloud className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Google Drive & Sheets</h2>
            <div className="flex items-center gap-2 mt-1">
              {connected ? (
                <><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-sm text-green-600 font-medium">Connecté</span></>
              ) : oauthConfigured ? (
                <><XCircle className="w-4 h-4 text-amber-500" /><span className="text-sm text-amber-600 font-medium">Identifiants OK, non connecté</span></>
              ) : (
                <><XCircle className="w-4 h-4 text-slate-400" /><span className="text-sm text-slate-500">Non configuré</span></>
              )}
              {data.source === 'env' && <Badge variant="outline" className="text-xs">via env</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {connected ? (
            <Button variant="outline" onClick={onDisconnect} data-testid="google-disconnect-btn">
              Déconnecter Google
            </Button>
          ) : (
            <Button
              onClick={onConnect}
              disabled={!oauthConfigured}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="google-connect-btn"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Connecter Google
            </Button>
          )}
        </div>
      </div>

      {!oauthConfigured && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
          <b>Étape 1 :</b> renseignez votre <b>Client ID</b> et <b>Client Secret</b> Google ci-dessous.
          Si vous n'en avez pas encore, consultez le guide en bas de cette carte.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Client ID Google</label>
          <Input
            value={form.client_id}
            onChange={(e) => setForm((p) => ({ ...p, client_id: e.target.value }))}
            placeholder="123456789-abc...apps.googleusercontent.com"
            data-testid="google-client-id"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
            Client Secret Google {data.client_secret_set && <span className="normal-case font-normal text-slate-400">(défini — laissez vide pour conserver)</span>}
          </label>
          <div className="relative">
            <Input
              type={showSecret ? 'text' : 'password'}
              value={form.client_secret}
              onChange={(e) => setForm((p) => ({ ...p, client_secret: e.target.value }))}
              placeholder={data.client_secret_set ? '••••••••' : 'GOCSPX-...'}
              data-testid="google-client-secret"
            />
            <button type="button" onClick={() => setShowSecret((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Dynamic redirect URI to copy into Google Cloud */}
      <div className="mt-4 bg-blue-50/60 border border-blue-200 rounded-lg p-3">
        <p className="text-xs uppercase tracking-wider text-blue-700 font-semibold mb-1">URI de redirection autorisée</p>
        <p className="text-xs text-slate-600 mb-2">
          Copiez cette URI <b>EXACTEMENT</b> dans votre app Google Cloud Console &gt; Identifiants OAuth &gt; URIs de redirection autorisés.
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-white border border-blue-200 rounded px-2 py-1.5 text-xs font-mono text-slate-700 break-all">
            {data.redirect_uri}
          </code>
          <Button size="sm" variant="outline" onClick={copyRedirectUri} data-testid="google-copy-redirect">
            <Copy className="w-3.5 h-3.5 mr-1" /> Copier
          </Button>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button onClick={handleSave} disabled={saving} data-testid="google-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      {/* Migration: only show when connected */}
      {connected && (
        <div className="mt-5 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4" data-testid="google-migration-block">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                <FolderTree className="w-4 h-4 text-indigo-600" />
                Migration complète Drive & Sheets
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Crée toute l'arborescence Drive (par collectivité, client privé, sous-traitant, chantier)
                et alimente le classeur Sheets dashboard avec votre historique CRM.
                Idempotent : ne crée jamais de doublons.
              </p>
              {syncStatus?.last_migration_at && (
                <p className="text-[11px] text-slate-500 mt-1.5">
                  Dernière migration : {syncStatus.last_migration_at.slice(0, 16).replace('T', ' ')}
                </p>
              )}
              {syncStatus?.dashboard_url && (
                <a href={syncStatus.dashboard_url} target="_blank" rel="noreferrer"
                  className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-1">
                  Ouvrir le classeur dashboard <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            <Button
              onClick={runMigration}
              disabled={migrating}
              className="bg-indigo-600 hover:bg-indigo-700 shrink-0"
              data-testid="google-migrate-btn"
            >
              {migrating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {migrating ? 'Migration en cours…' : (syncStatus?.last_migration_at ? 'Re-synchroniser' : 'Lancer la migration')}
            </Button>
          </div>
          {/* Live progress bar while migration is running */}
          {progress?.running && (
            <div className="mt-3" data-testid="google-migration-progress">
              <div className="flex items-center justify-between text-xs text-slate-700 mb-1">
                <span className="font-medium truncate" data-testid="migration-step">{progress.step || 'En cours…'}</span>
                <span className="tabular-nums text-slate-500" data-testid="migration-percent">
                  {progress.percent ?? 0}%
                  {progress.total ? ` · ${progress.current ?? 0}/${progress.total}` : ''}
                </span>
              </div>
              <div className="h-2 w-full bg-white/70 rounded-full border border-indigo-200 overflow-hidden">
                <div
                  className="h-full bg-indigo-600 transition-all duration-500 ease-out"
                  style={{ width: `${Math.max(2, Math.min(100, progress.percent ?? 0))}%` }}
                />
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                La migration tourne en arrière-plan. Vous pouvez quitter cette page, elle continuera côté serveur.
              </p>
            </div>
          )}
          {!progress?.running && progress?.error && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800" data-testid="migration-error">
              Dernière migration en erreur : {progress.error}
            </div>
          )}
          {lastSummary && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="google-migration-summary">
              <Stat label="Clients" value={lastSummary.contacts} />
              <Stat label="Chantiers" value={lastSummary.chantiers} />
              <Stat label="Sous-traitants" value={lastSummary.sous_traitants} />
              <Stat label="Devis" value={lastSummary.devis} />
              <Stat label="Factures émises" value={lastSummary.factures} />
              <Stat label="Factures d'achat" value={lastSummary.factures_achat} />
              {lastSummary.errors?.length > 0 && (
                <Stat label="Erreurs" value={lastSummary.errors.length} warn />
              )}
            </div>
          )}
        </div>
      )}

      {/* Setup guide */}
      <div className="mt-4 border-t border-slate-200 pt-3">
        <button type="button" onClick={() => setShowHelp((v) => !v)} className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-700">
          <HelpCircle className="w-4 h-4" />
          Comment obtenir mes identifiants Google OAuth ?
          {showHelp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showHelp && (
          <div className="mt-3 bg-blue-50/60 border border-blue-200 rounded-lg p-4 text-sm text-slate-700 space-y-3">
            <div>
              <p className="font-semibold text-slate-900 mb-1">1. Créer un projet Google Cloud</p>
              <ol className="list-decimal list-inside space-y-0.5 ml-1 text-slate-600">
                <li>Allez sur <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">console.cloud.google.com <ExternalLink className="w-3 h-3" /></a></li>
                <li>Créez un nouveau projet (ex : « Reno Resolve CRM »)</li>
              </ol>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">2. Activer les APIs Drive et Sheets</p>
              <ol className="list-decimal list-inside space-y-0.5 ml-1 text-slate-600">
                <li>Menu &gt; APIs et services &gt; Bibliothèque</li>
                <li>Recherchez et activez : <b>Google Drive API</b></li>
                <li>Puis : <b>Google Sheets API</b></li>
              </ol>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">3. Configurer l'écran de consentement OAuth</p>
              <ol className="list-decimal list-inside space-y-0.5 ml-1 text-slate-600">
                <li>Menu &gt; APIs et services &gt; Écran de consentement OAuth</li>
                <li>Type d'utilisateur : <b>Externe</b> (puis Créer)</li>
                <li>Nom de l'application, email d'assistance : remplissez vos infos</li>
                <li>Domaines autorisés : <code className="bg-white px-1 rounded text-xs">renoresolve.com</code></li>
                <li>Ajoutez votre email dans <b>Utilisateurs test</b> (sinon vous serez bloqué)</li>
              </ol>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">4. Créer un ID client OAuth 2.0</p>
              <ol className="list-decimal list-inside space-y-0.5 ml-1 text-slate-600">
                <li>Menu &gt; APIs et services &gt; <b>Identifiants</b></li>
                <li>+ Créer des identifiants &gt; <b>ID client OAuth 2.0</b></li>
                <li>Type d'application : <b>Application Web</b></li>
                <li>Nom : « Reno Resolve CRM »</li>
                <li>URIs de redirection autorisés : <b>collez l'URI affichée plus haut</b> ☝️ (bouton "Copier")</li>
                <li>Créer → un popup affiche votre <b>Client ID</b> et <b>Client Secret</b></li>
                <li>Copiez-les dans les champs ci-dessus, cliquez <b>Enregistrer</b></li>
              </ol>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">5. Cliquer sur « Connecter Google »</p>
              <p className="ml-1 text-slate-600">Vous serez redirigé vers Google, autorisez l'accès Drive + Sheets, et vous revenez ici connecté. Les fichiers s'enregistreront dans votre Drive.</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
              ⚠️ Tant que votre app est en mode « test », seuls les utilisateurs test peuvent se connecter. Pour la rendre publique, demandez la vérification à Google (peut prendre quelques jours).
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div className={`rounded-lg px-2 py-1.5 border ${warn ? 'bg-red-50 border-red-200' : 'bg-white border-indigo-100'}`}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className={`text-sm font-bold ${warn ? 'text-red-700' : 'text-slate-900'}`}>{value ?? 0}</p>
    </div>
  );
}
